/**
 * AK-446 Ш1 — блок «Что уже сделали в поле»: явные действия по посеву.
 *
 * Зачем
 * -----
 * До сих пор жизненный цикл посева нельзя было пройти в UI целиком: «посеять»
 * = тап по чипу полосы прогресса (без даты и без подтверждения — легко
 * промахнуться и не заметить), «убрать» и «списать» из карточки не делались
 * вовсе. Вкладка при этом смешивает два разных занятия — «решаем, что посеем»
 * (форма планирования) и «отмечаем, что уже сделали» (факт). Пока они в одном
 * потоке, любая кнопка читается как «сохранить план».
 *
 * Поэтому факт-действия вынесены в отдельный блок НАД формой планирования, с
 * одним правилом: показываем действия только текущего статуса
 * (`@/lib/sowings/actions`, матрица утверждённого wireframe
 * `docs/superpowers/prototypes/ak-446-sowing-actions/`, founder 31.07 → #601).
 *
 * Чего здесь осознанно НЕТ
 * -----------------------
 * · **Полосы этапов** — она уже живёт в форме ниже (`SowForm` →
 *   `OccupancyProgress`). Wireframe рисует её внутри блока, но две полосы на
 *   одном экране хуже, чем одна не на своём месте.
 *
 * «Списать» (Ш2) ведёт в `WriteoffActDialog` — акт списания
 * (`crop_writeoff_acts`): смерть занятия оформляется документом с причиной,
 * а не отметкой (ADR-059 §D8).
 *
 * Отклонение от wireframe, которое нужно знать ревьюеру
 * ----------------------------------------------------
 * Wireframe v4 (упрощение founder 31.07) говорит: «Посеять»/«Убрать» открывают
 * **существующую форму тех.операции** «Сев»/«Уборка» с префилом, а статус
 * меняется автоматически по факту операции. Такой формы во фронте **пока не
 * существует** (`pages/operations/` — только список, создание операции строит
 * вертикаль operations параллельно), а событийная деривация статуса из
 * операций — отдельный эпик ADR-059 §D1.
 *
 * Поэтому Ш1 использует **второй, явно разрешённый тем же wireframe путь**:
 * «уборка "на бегу" без операции остаётся возможной — ручная отметка с
 * пометкой "нет подтверждающей тех.операции"» (аннотация 3 экрана Д). Кнопка
 * спрашивает дату события и подтверждает действие словами — то есть даёт
 * ровно то, чего не хватало тапу по полосе. Когда форма операции появится,
 * кнопка переедет на неё без изменения этого блока: матрица действий и
 * раскладка остаются, меняется только обработчик.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SowingStatusBadge } from '@/components/fields/SowingStatusBadge';
import {
  SOWING_ACTION_LABELS,
  isPrimaryAction,
  sowingActions,
} from '@/lib/sowings/actions';
import {
  sowingStage,
  sowingStageLabel,
  sowingStageQuantity,
} from '@/lib/sowings/stage';
import { useSetSowingStatus } from '@/lib/sowings/statusApi';
import {
  activeWriteoffAct,
  useCancelWriteoffAct,
  useWriteoffActs,
} from '@/lib/sowings/writeoffApi';
import { WriteoffActDialog } from './WriteoffActDialog';
import type { OccupancySowing } from './types';

/** Сегодня в формате `<input type="date">`, по местному времени. */
function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Что пишем в статус и как объясняем последствие — по одному действию. */
const ACTION_EFFECT: Record<
  'sow' | 'harvest',
  { status: 'growing' | 'harvested'; dateLabel: string; effect: string }
> = {
  sow: {
    status: 'growing',
    dateLabel: 'Дата сева',
    effect: 'Посев перейдёт в «Растёт» — культура в поле.',
  },
  harvest: {
    status: 'harvested',
    dateLabel: 'Дата уборки',
    effect: 'Посев перейдёт в «Убрано» — сезон по нему закрыт.',
  },
};

interface Props {
  /** Занятие leaf'а. `null` — посева на сезон нет (это не статус, а его отсутствие). */
  sowing: OccupancySowing | null;
  /** Подпись культуры/пара для заголовка диалога — то же слово, что видит агроном. */
  title: string;
  canWrite: boolean;
  /** «Создать посев» — якорь к форме планирования ниже, не модалка. */
  onCreate?: () => void;
  /**
   * Почему «Создать посев» сейчас недоступно (AK-549). Задан — кнопка гасится
   * и объясняется тултипом ТЕМ ЖЕ текстом, которым ответил бы сервер (409).
   *
   * Гасим только создание: если на участке уже есть посев, «Убрать» — это как
   * раз выход из тупика, отнимать его нельзя.
   */
  createDisabledReason?: string | null;
  /** Компактный вид для строки зоны (экран Ж wireframe). */
  compact?: boolean;
  /**
   * Сезон выбранного посева завершён (даты рамки прошли, канон v3 §5).
   * Пробрасывается в бейдж статуса: «Запланировано» в прошедшем сезоне
   * читается как «Не реализован».
   */
  seasonCompleted?: boolean;
  className?: string;
}

export function SowingActions({
  sowing,
  title,
  canWrite,
  onCreate,
  createDisabledReason = null,
  compact = false,
  seasonCompleted = false,
  className,
}: Props) {
  const [pending, setPending] = useState<'sow' | 'harvest' | null>(null);
  const [writeoffOpen, setWriteoffOpen] = useState(false);
  const [occurredOn, setOccurredOn] = useState<string>(today());
  const sowingId = sowing?.sowing_id ?? '';
  const setStatus = useSetSowingStatus(sowingId);

  // Акты запрашиваем ТОЛЬКО у терминального «Списано»: у растущего занятия их
  // нет по определению, а лишний запрос на каждую строку зоны — это те самые
  // микро-задержки, из которых складывается усталость от системы.
  const isWrittenOff = sowingStage(sowing) === 'written_off';
  const acts = useWriteoffActs(isWrittenOff ? sowingId : null);
  const cancelAct = useCancelWriteoffAct(sowingId);
  const confirmedAct = activeWriteoffAct(acts.data);

  const actions = sowingActions(sowing);

  // Прав нет — блок не показываем вовсе (как и форму: баннер «Недостаточно
  // прав» на вкладке уже есть, второй раз объяснять не нужно).
  if (!canWrite) return null;
  if (!actions.length && !sowing) return null;

  const stage = sowingStage(sowing);
  const quantity = sowingStageQuantity(sowing);

  const open = (code: 'sow' | 'harvest') => {
    setOccurredOn(today());
    setPending(code);
  };

  const submit = async () => {
    if (!pending || !occurredOn) return;
    await setStatus.mutateAsync({
      status: ACTION_EFFECT[pending].status,
      occurred_on: occurredOn,
    });
    setPending(null);
  };

  return (
    <div
      className={cn(
        compact
          ? 'flex flex-wrap items-center gap-2'
          : 'space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-4',
        className,
      )}
    >
      {!compact && (
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Что уже сделали в поле
        </div>
      )}

      <div
        className={cn(
          'flex flex-wrap items-center gap-3',
          !compact && 'justify-between',
        )}
      >
        {!compact && (
          <div className="min-w-0 space-y-2">
            <div className="text-sm font-medium">{title}</div>
            {sowing ? (
              <div className="flex flex-wrap items-center gap-2">
                <SowingStatusBadge
                  sowingId={sowing.sowing_id}
                  status={stage}
                  note={sowing.crop_status_note}
                  canWrite={canWrite}
                  seasonCompleted={seasonCompleted}
                />
                {quantity && (
                  <span className="text-xs text-muted-foreground">
                    {quantity}
                  </span>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                На этот сезон посева нет
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {actions.map((code) => {
            const blocked = code === 'create' && Boolean(createDisabledReason);
            const btn = (
              <Button
                size={compact ? 'sm' : 'default'}
                variant={isPrimaryAction(code) ? 'default' : 'outline'}
                disabled={blocked}
                onClick={() => {
                  if (code === 'create') return onCreate?.();
                  if (code === 'writeoff') return setWriteoffOpen(true);
                  return open(code);
                }}
              >
                {SOWING_ACTION_LABELS[code]}
              </Button>
            );
            if (!blocked) return <span key={code}>{btn}</span>;
            // Disabled-кнопка не получает pointer-событий, поэтому триггером
            // тултипа служит обёртка. `tabIndex`/`title` — чтобы объяснение
            // доставалось и с клавиатуры, и там, где hover'а нет вовсе
            // (тач): правило «hover-only не делаем» (CLAUDE.md).
            return (
              <Tooltip key={code}>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    title={createDisabledReason ?? undefined}
                    className="inline-flex cursor-not-allowed"
                  >
                    {btn}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {createDisabledReason}
                </TooltipContent>
              </Tooltip>
            );
          })}
          {/* Терминал: объясняем, почему кнопок нет, и куда идти, если ошиблись.
              Пустое место на их месте читается как «сломалось». */}
          {!compact && sowing && !actions.length && !confirmedAct && (
            <span className="max-w-xs text-xs text-muted-foreground">
              Сезон по этому посеву закрыт. Ошиблись — поправьте статус в
              бейдже, там запишется причина.
            </span>
          )}
        </div>
      </div>

      {/* Списано по акту — показываем сам документ и даём его отменить.
          Без этой строки «Списано» выглядит как приговор без объяснения:
          агроном не видит ни причины, ни того, что решение обратимо. */}
      {!compact && confirmedAct && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2 text-xs text-muted-foreground">
          <span>
            Акт списания
            {confirmedAct.confirmed_at
              ? ` от ${new Date(confirmedAct.confirmed_at).toLocaleDateString('ru-RU')}`
              : ''}
            {confirmedAct.reason_name ? ` · ${confirmedAct.reason_name}` : ''}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={cancelAct.isPending}
            onClick={() =>
              void cancelAct.mutateAsync({ actId: confirmedAct.id })
            }
          >
            {cancelAct.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Отменить акт
          </Button>
        </div>
      )}

      <Dialog
        open={pending != null}
        onOpenChange={(v) => !v && setPending(null)}
      >
        <DialogContent className="sm:max-w-md">
          {pending && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {SOWING_ACTION_LABELS[pending]} · {title}
                </DialogTitle>
                <DialogDescription>
                  Отметка без тех.операции: рядом со статусом будет пометка
                  «нет подтверждающей тех.операции».
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="sowing-action-date">
                  {ACTION_EFFECT[pending].dateLabel}
                </Label>
                <Input
                  id="sowing-action-date"
                  type="date"
                  value={occurredOn}
                  onChange={(e) => setOccurredOn(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Дата события, а не дата нажатия — она попадёт в журнал.
                </p>
              </div>

              <div className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                <b className="text-foreground">После сохранения: </b>
                {ACTION_EFFECT[pending].effect}
                {stage && (
                  <>
                    {' '}
                    Сейчас — «{sowingStageLabel(sowing)}».
                  </>
                )}
              </div>

              {setStatus.isError && (
                <p className="text-xs text-destructive">
                  Не удалось сохранить. Попробуйте ещё раз.
                </p>
              )}

              <DialogFooter>
                <Button variant="ghost" onClick={() => setPending(null)}>
                  Отмена
                </Button>
                <Button
                  onClick={() => void submit()}
                  disabled={!occurredOn || setStatus.isPending}
                >
                  {setStatus.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {SOWING_ACTION_LABELS[pending]}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {sowing && (
        <WriteoffActDialog
          open={writeoffOpen}
          onOpenChange={setWriteoffOpen}
          sowingId={sowing.sowing_id}
          title={title}
        />
      )}
    </div>
  );
}
